import { ReactNode } from 'react';
import { Group, GroupProps, composeRenderProps } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';

export interface ClarionFormGroupProps extends Omit<GroupProps, 'children'> {
  label?: ReactNode;
  labelFor?: string;
  labelInfo?: ReactNode;
  helperText?: ReactNode;
  intent?: 'none' | 'primary' | 'success' | 'warning' | 'danger';
  inline?: boolean;
  fill?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export function ClarionFormGroup({
  label,
  labelFor,
  labelInfo,
  helperText,
  intent = 'none',
  inline = false,
  fill = false,
  disabled = false,
  children,
  className,
  ...props
}: ClarionFormGroupProps) {
  const intentStyles = {
    none: '',
    primary: 'text-purple-600 dark:text-purple-300',
    success: 'text-green-600 dark:text-green-300',
    warning: 'text-orange-600 dark:text-orange-300',
    danger: 'text-red-600 dark:text-red-300',
  };

  return (
    <Group
      {...props}
      className={composeRenderProps(className, (className: string | undefined) =>
        twMerge(
          'flex flex-col gap-1',
          inline && 'flex-row items-center gap-4',
          disabled && 'opacity-50 pointer-events-none',
          className,
        ),
      )}
    >
      {label && (
        <div className={twMerge('flex items-center gap-2', inline && 'min-w-0 shrink-0')}>
          <label
            htmlFor={labelFor}
            className={twMerge(
              'text-sm font-medium cursor-default',
              'text-gray-700 dark:text-gray-200',
              intent !== 'none' && intentStyles[intent],
            )}
          >
            {label}
          </label>
          {labelInfo && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{labelInfo}</span>
          )}
        </div>
      )}
      <div className={twMerge(fill && 'flex-1', inline && 'min-w-0')}>{children}</div>
      {helperText && (
        <div
          className={twMerge(
            'text-xs text-gray-600 dark:text-gray-400',
            intent !== 'none' && intentStyles[intent],
          )}
        >
          {helperText}
        </div>
      )}
    </Group>
  );
}
